/**
 * Hitscan (railgun) resolution: a ray from the shooter's muzzle, stopped by the
 * first player hitbox or the terrain. Pure — the game draws the beam and logs
 * from the result; the headless harness applies it directly.
 */

import type { Vec3, PlayerState, PlanetState, FireParams, GameState } from '../types';
import { applyDamage } from './damage';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize } from '../math';
import { getSurfaceRadius, raycastTerrain } from './surface';
import { computeLaunchDirection, getPlayerWorldPosition } from './gameState';

export const HITSCAN_MAX_RANGE = 100;
/** Player hitbox for hitscan: a sphere centred 0.5 above the feet. */
export const HITSCAN_HITBOX_RADIUS = 0.6;
export const HITSCAN_HITBOX_HEIGHT = 0.5;
/** Muzzle height above the terrain. */
export const HITSCAN_MUZZLE_HEIGHT = 1.2;

export interface HitscanResult {
  startPos: Vec3;
  hitPoint: Vec3;
  /** The player hit, or null when the beam ended on terrain or ran out of range. */
  hitPlayerId: number | null;
  distance: number;
}

/** Trace the beam. `friendlyFire === 0` skips teammates' hitboxes. */
export function raycastHitscan(
  shooter: PlayerState,
  params: FireParams,
  players: PlayerState[],
  planets: PlanetState[],
  friendlyFire: number,
): HitscanResult {
  const planet = planets[shooter.planetIndex];
  const surfaceNormal = vecNormalize(shooter.surfaceDir);
  const surfaceRadius = getSurfaceRadius(planet, shooter.surfaceDir);
  const worldPos = vecAdd(planet.position, vecScale(surfaceNormal, surfaceRadius));
  const startPos = vecAdd(worldPos, vecScale(surfaceNormal, HITSCAN_MUZZLE_HEIGHT));
  const launchDir = computeLaunchDirection(surfaceNormal, params.azimuth, params.elevation);

  let closest = HITSCAN_MAX_RANGE;
  let hitPlayerId: number | null = null;
  let hitPoint: Vec3 = vecAdd(startPos, vecScale(launchDir, HITSCAN_MAX_RANGE));

  for (const other of players) {
    if (other.id === shooter.id) continue;
    if (!other.alive || other.airborne) continue;
    if (friendlyFire === 0 && other.teamIndex === shooter.teamIndex) continue;

    const otherPos = getPlayerWorldPosition(other, planets);
    const otherNormal = vecNormalize(other.surfaceDir);
    const centre = vecAdd(otherPos, vecScale(otherNormal, HITSCAN_HITBOX_HEIGHT));

    // Ray-sphere: |start + t·dir − centre|² = r²
    const oc = vecSub(startPos, centre);
    const b = vecDot(oc, launchDir);
    const c = vecDot(oc, oc) - HITSCAN_HITBOX_RADIUS * HITSCAN_HITBOX_RADIUS;
    const disc = b * b - c;
    if (disc >= 0) {
      const t = -b - Math.sqrt(disc);
      if (t > 0 && t < closest) {
        closest = t;
        hitPlayerId = other.id;
        hitPoint = vecAdd(startPos, vecScale(launchDir, t));
      }
    }
  }

  // Exact mesh raycast — beams do not pass through hills
  const terrainHit = raycastTerrain(startPos, launchDir, closest, planets);
  if (terrainHit && terrainHit.distance < closest) {
    closest = terrainHit.distance;
    hitPlayerId = null;
    hitPoint = terrainHit.point;
  }

  return { startPos, hitPoint, hitPlayerId, distance: closest };
}

/** Apply a hitscan hit: damage the target, credit the shooter. Returns the new state and whether the target died. */
export function applyHitscanDamage(
  state: GameState,
  shooterId: number,
  targetId: number,
  damage: number,
): { state: GameState; killed: boolean; newHealth: number } {
  // the one damage door: armor off the beam, the shooter credited (core/damage.ts applyDamage)
  const r = applyDamage(state, { unit: targetId }, damage, 'bullet', shooterId);
  return { state: r.state, killed: r.killed, newHealth: r.state.players.find(p => p.id === targetId)?.health ?? 0 };
}
